import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getLogs } from "../utils/api";

import androidPng from "../assets/android.png";
import iosPng from "../assets/ios.png";
import pcPng from "../assets/pc.png";

/** --------- Icon Helpers (normal + active) ---------- */
const makeIcon = (url, size = 28, className = "qr-marker") =>
  new L.Icon({
    iconUrl: url,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -Math.max(20, size - 8)],
    className,
  });

const iconSet = {
  android: {
    normal: makeIcon(androidPng, 28, "qr-marker"),
    active: makeIcon(androidPng, 36, "qr-marker is-active"),
  },
  apple: {
    normal: makeIcon(iosPng, 28, "qr-marker"),
    active: makeIcon(iosPng, 36, "qr-marker is-active"),
  },
  other: {
    normal: makeIcon(pcPng, 28, "qr-marker"),
    active: makeIcon(pcPng, 36, "qr-marker is-active"),
  },
};

const iconFor = (os = "", active = false) => {
  const key = /android/i.test(os) ? "android" : /(ios|iphone|ipad|apple|mac)/i.test(os) ? "apple" : "other";
  return active ? iconSet[key].active : iconSet[key].normal;
};

/** --------- Utils ---------- */
const toNum = (v) => (v === null || v === undefined ? null : Number(v));
const hasCoords = (s) => Number.isFinite(toNum(s.lat)) && Number.isFinite(toNum(s.lon));
const osFromUA = (ua) => {
  const x = (ua || "").toLowerCase();
  if (x.includes("android")) return "Android";
  if (/(iphone|ipad|mac|ios|apple)/.test(x)) return "Apple";
  return "Unknown";
};

/** stabile ID aus Daten (ohne Listenindex) */
const makeId = (s) => {
  const a = s.timestamp || "";
  const b = s.ip || "";
  const c = s.scanId || s.qrCode || "unknown";
  return `${a}|${b}|${c}`;
};

export default function MapComponent() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [qrSel, setQrSel] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [justRefreshed, setJustRefreshed] = useState(false);

  const mapRef = useRef(null);
  const markerRefs = useRef(new Map()); // id -> ref

  /** Laden + Normalisieren */
  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getLogs(120);
      const norm = (data || []).map((s) => {
        const os = s.os || osFromUA(s.userAgent);
        const row = {
          id: makeId(s),
          scanId: s.scanId || s.qrCode || "unknown",
          timestamp: s.timestamp,
          os,
          device: s.device || (os === "Android" ? "Android" : os === "Apple" ? "iOS" : "Desktop"),
          city: s.city || s.geo?.city || "",
          region: s.region || s.geo?.region || "",
          country: s.country || s.geo?.country || "",
          lat: s.lat ?? s.latitude ?? s.geo?.lat ?? null,
          lon: s.lon ?? s.lng ?? s.longitude ?? s.geo?.lon ?? null,
          ip: s.ip,
          userAgent: s.userAgent,
        };
        return row;
      });
      setRows(norm);

      // Map Resize + Button-Flash
      setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 50);
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 800);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const id = setInterval(loadData, 15000);
    return () => clearInterval(id);
  }, []);

  /** QR-Optionen */
  const qrOptions = useMemo(() => {
    const set = new Map();
    for (const r of rows) set.set(r.scanId || "unknown", (set.get(r.scanId || "unknown") || 0) + 1);
    return Array.from(set.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [rows]);

  /** Filter */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const byQr = !qrSel || r.scanId === qrSel;
      const byText =
        !q ||
        String(r.scanId).toLowerCase().includes(q) ||
        String(r.city).toLowerCase().includes(q) ||
        String(r.country).toLowerCase().includes(q) ||
        String(r.ip || "").toLowerCase().includes(q);
      return byQr && byText;
    });
  }, [rows, query, qrSel]);

  /** Punkte */
  const points = useMemo(() => filtered.filter(hasCoords), [filtered]);

  /** Fit auf Punkte (wenn keine Auswahl aktiv ist) */
  useEffect(() => {
    if (!mapRef.current || selectedId) return;
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [Number(p.lat), Number(p.lon)]));
      mapRef.current.fitBounds(bounds, { padding: [20, 20] });
    } else {
      mapRef.current.setView([51.1657, 10.4515], 5);
    }
  }, [points, selectedId]);

  /** Auswahl -> sanft zoomen + Popup öffnen (Fallback, falls man direkt aus Code setzen will) */
  useEffect(() => {
    if (!selectedId) return;
    const item = rows.find((r) => r.id === selectedId);
    if (!item || !hasCoords(item) || !mapRef.current) return;
    const center = [Number(item.lat), Number(item.lon)];
    mapRef.current.flyTo(center, 13, { animate: true, duration: 1.2 });

    const mRef = markerRefs.current.get(item.id);
    if (mRef?.current) setTimeout(() => mRef.current.openPopup(), 350);
  }, [selectedId, rows]);

  /** Direktes Handling beim Klick in der Liste (sofortiges Fly + Popup + Highlight) */
  const onRowClick = (r) => {
    if (!hasCoords(r)) return;
    setSelectedId(r.id); // Highlight + Marker-Icon wechselt

    if (mapRef.current) {
      const center = [Number(r.lat), Number(r.lon)];
      mapRef.current.flyTo(center, 13, { animate: true, duration: 1.0 });
    }
    const mRef = markerRefs.current.get(r.id);
    if (mRef?.current) setTimeout(() => mRef.current.openPopup(), 350);
  };

  return (
    <div className="page">
      {/* Filterleiste */}
      <div className="filter-card">
        <button
          className={`btn btn--primary ${justRefreshed ? "btn--flash" : ""}`}
          onClick={loadData}
          disabled={loading}
          title="Neu laden"
        >
          {loading ? "Aktualisiere…" : "Aktualisieren"}
        </button>

        <input
          className="input input--grow"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nach Ort / QR / IP suchen…"
        />

        <select
          className="select"
          value={qrSel}
          onChange={(e) => setQrSel(e.target.value)}
          title="Nach QR-Code filtern"
        >
          <option value="">Alle QR-Codes</option>
          {qrOptions.map(({ code, count }) => (
            <option key={code} value={code}>
              {code} ({count})
            </option>
          ))}
        </select>

        <button className="btn" onClick={() => { setQrSel(""); setQuery(""); }}>
          Alle QR-Codes
        </button>

        <div className="muted small" style={{ marginLeft: "auto" }}>
          Marker: {points.length} / Items: {filtered.length}
        </div>
      </div>

      {/* Map oben sticky, Liste scrollt */}
      <div className="map-page" style={{ marginTop: 10 }}>
        <div className="map-sticky sticky">
          <MapContainer
            center={[51.1657, 10.4515]}
            zoom={5}
            style={{ height: "100%", width: "100%" }}
            whenCreated={(map) => (mapRef.current = map)}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {points.map((p) => {
              if (!markerRefs.current.has(p.id)) markerRefs.current.set(p.id, React.createRef());
              const ref = markerRefs.current.get(p.id);
              const active = p.id === selectedId;

              return (
                <Marker
  key={p.id}
  ref={ref}
  position={[Number(p.lat), Number(p.lon)]}
  icon={iconFor(p.os, p.id === selectedId)}
  eventHandlers={{
    // Marker-Tap: auswählen
    click: () => setSelectedId(p.id),

    // Falls jemand direkt auf den Marker tippt (ohne Liste):
    popupopen: () => setSelectedId(p.id),

    // X im Popup oder Klick ins Leere → Highlight wieder aus
    popupclose: () => setSelectedId(prev => (prev === p.id ? null : prev)),
  }}
                >
                  <Popup>
                    <div style={{ minWidth: 180 }}>
                      <b>{p.device}</b> ({p.os})<br />
                      QR: {p.scanId}<br />
                      {([p.city, p.region, p.country].filter(Boolean).join(", ") || "?")}<br />
                      {p.timestamp}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        <div className="list-scroll">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Ort</th>
                  <th>QR-Code</th>
                  <th>Gerät</th>
                  <th className="hide-sm">OS</th>
                  <th className="hide-sm">Zeit</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const place = [r.city, r.region, r.country].filter(Boolean).join(", ") || "?";
                  const clickable = hasCoords(r);
                  return (
                    <tr
                      key={r.id}
                      onClick={() => clickable && onRowClick(r)}
                      className={r.id === selectedId ? "is-selected" : ""}
                      title={clickable ? "Klicken um auf Marker zu zoomen" : "Keine Koordinaten vorhanden"}
                      style={{ cursor: clickable ? "pointer" : "default" }}
                    >
                      <td data-label="Ort">{place}</td>
                      <td data-label="QR-Code">{r.scanId}</td>
                      <td data-label="Gerät">{r.device}</td>
                      <td data-label="OS" className="hide-sm">{r.os}</td>
                      <td data-label="Zeit" className="hide-sm">{r.timestamp}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
